import { format, Locale } from "date-fns";
import { enUS, fr, sv } from "date-fns/locale";
 // eslint-disable-next-line @typescript-eslint/no-unused-vars
import { useState, useEffect, useCallback } from "react";
 // eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Link, useNavigate } from "react-router-dom";
import { removePackageFromCart } from "./ShoppingCartService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShoppingCart, faTrash } from "@fortawesome/free-solid-svg-icons";



interface NewCartDropDownProps {
  cartItems: any[];
  setCartItems: (items: any[]) => void;
  fetchCartItems: () => void;
  isDropdownOpen: boolean;
  setIsDropdownOpen: (isOpen: boolean) => void;
}

const localeMap: { [key: string]: Locale } = {
  "en-US": enUS,
  "fr-FR": fr,
  "sv-SE": sv,
  sv: sv, // Add short code for Swedish
  // Add more mappings as needed
};
 // eslint-disable-next-line @typescript-eslint/no-unused-vars
const currencyMap: { [key: string]: string } = {
  "en-US": "USD",
  "fr-FR": "EUR",
  "sv-SE": "SEK",
  sv: "SEK", // Add short code for Swedish
  // Add more mappings as needed
};

const NewCartDropDown: React.FC<NewCartDropDownProps> = ({
  cartItems,
  setCartItems,
  fetchCartItems,
  isDropdownOpen,
  setIsDropdownOpen,
}) => {
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [exchangeRates, setExchangeRates] = useState<{ [key: string]: number }>(
    {}
  );
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  // Retrieve the user's language and currency settings
  const userLanguage =
    localStorage.getItem("userLanguage") || navigator.language || "en-US";
  const userLocale = localeMap[userLanguage] || enUS;
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const userCurrency = "EUR"; // Fixed to EUR
 // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sessionId = localStorage.getItem("sessionId");

  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        const response = await fetch(
          "https://api.exchangerate-api.com/v4/latest/EUR"
        );
        if (!response.ok) {
          throw new Error("Failed to fetch exchange rates");
        }
        const data = await response.json();
        setExchangeRates(data.rates);
      } catch (error) {
        console.error("Error fetching exchange rates:", error);
      }
    };

    fetchExchangeRates();
  }, []);

  useEffect(() => {
    console.log("Cart Items:", cartItems); // Add logging to verify cartItems
  }, [cartItems]);

  const formatDate = (date: string) => {
    return format(new Date(date), "PPP", { locale: userLocale });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const handleCheckout = () => {
    navigate('/checkout');
    setIsDropdownOpen(false);
  };

  const handleRemoveItem = async (packageId: number, itemId: number) => {
    try {
      const sessionId = localStorage.getItem("sessionId");
      if (!sessionId) {
        throw new Error("Session ID not found");
      }

      console.log(
        "Removing item with sessionId:",
        sessionId,
        "packageId:",
        packageId,
        "itemId:",
        itemId
      );

      const updatedCart = await removePackageFromCart(
        sessionId,
        packageId,
        itemId
      );
      if (updatedCart && Array.isArray(updatedCart.items)) {
        const updatedCartItems = updatedCart.items.filter(
          (item: any) => item.id !== itemId
        );
        setCartItems(updatedCartItems);
      } else {
        setCartItems([]);
      }
      fetchCartItems(); // Trigger fetch to update cart items
    } catch (error) {
      console.error("Error removing item from cart:", error);
    }
  };

  return (
    <div className={`cart-dropdown ${isDropdownOpen ? "open" : ""}`}>
      {cartItems.length > 0 ? (
        <>
          <ul>
            {cartItems.map((item, index) => (
              <li key={index} className="cart-item">
                <img
                  src={item.mainPictureUrl}
                  alt={item.name}
                  className="cart-item-image"
                />
                <div className="cart-item-details">
                  <div>
                    <strong>Package: </strong>
                    <span className="cart-item-name">
                      <a
                        href={item.packageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.packageName}
                      </a>
                    </span>
                  </div>
                  <div>
                    <strong>Category:</strong> {item.categoryName}
                  </div>
                  <div>
                    <strong>Option:</strong> {item.pricingModelDescription}
                  </div>
                  <div>
                    <strong>Arrival Date:</strong>{" "}
                    {formatDate(item.arrivalDate)}
                  </div>
                  <div>
                    <strong>Departure Date:</strong>{" "}
                    {formatDate(item.departureDate)}
                  </div>
                  <div>
                    <strong>Price Per Person:</strong>{" "}
                    {formatCurrency(
                      item.maxPersons > 0
                        ? item.totalPrice / item.maxPersons
                        : item.totalPrice
                    )}
                  </div>
                  <div>
                    <strong>Total Price:</strong>{" "}
                    {formatCurrency(item.totalPrice)}
                  </div>
                  <button
                    className="remove-button icon-button"
                    onClick={() => handleRemoveItem(item.packageId, item.id)}
                  >
                    <FontAwesomeIcon icon={faTrash} className="icon" /> Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <div className="cart-summary">
            <strong>Total:</strong>{" "}
            {formatCurrency(
              cartItems.reduce((total, item) => total + item.totalPrice, 0)
            )}
            <button
              className="checkout-button icon-button"
              onClick={handleCheckout}
            >
              <FontAwesomeIcon icon={faShoppingCart} className="icon" />{" "}
              Checkout
            </button>
          </div>
        </>
      ) : (
        <p>Your cart is empty</p>
      )}
    </div>
  );
};

export default NewCartDropDown;
